import React, { useMemo, useState } from 'react';
import type {
  SpendApprovalPerson,
  SpendApprovalPersonEdit,
  SpendApprovalPersonField,
} from '@dreamai/types';
import { CheckIcon, PencilIcon } from './transactional-icons';

/**
 * The people a purchase is issued to, as they will print on the ticket.
 * People with `fields` can be corrected in place (behind a pencil); edits
 * are returned as `SpendApprovalPersonEdit[]` for the backend to validate
 * and book with. People without `fields` render read-only name + facts.
 */

export type PersonValues = Record<string, string>[];

export const initialPersonValues = (
  people: SpendApprovalPerson[],
): PersonValues =>
  people.map((person) =>
    Object.fromEntries((person.fields ?? []).map((f) => [f.key, f.value])),
  );

/** Only the fields whose value differs from the proposal, per person. */
export const personEdits = (
  people: SpendApprovalPerson[],
  values: PersonValues,
): SpendApprovalPersonEdit[] =>
  people.flatMap((person, index) => {
    const changed = Object.fromEntries(
      (person.fields ?? [])
        .filter((f) => (values[index]?.[f.key] ?? f.value) !== f.value)
        .map((f) => [f.key, (values[index]?.[f.key] ?? '').trim()]),
    );
    return Object.keys(changed).length > 0 ? [{ index, fields: changed }] : [];
  });

/** Required fields still empty, as "Name · Field" labels. */
export const missingPersonFields = (
  people: SpendApprovalPerson[],
  values: PersonValues,
): string[] =>
  people.flatMap((person, index) =>
    (person.fields ?? [])
      .filter((f) => f.required && !(values[index]?.[f.key] ?? '').trim())
      .map((f) => f.label),
  );

const inputType = (type: SpendApprovalPersonField['type']) => {
  switch (type) {
    case 'date':
      return 'date';
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    default:
      return 'text';
  }
};

const placeholder = (field: SpendApprovalPersonField) => {
  switch (field.type) {
    case 'date':
      return 'YYYY-MM-DD';
    case 'phone':
      return '+44 7700 900123';
    case 'country':
      return 'e.g. GB';
    default:
      return '';
  }
};

const displayName = (
  person: SpendApprovalPerson,
  values: Record<string, string>,
) => {
  const first = values.firstName ?? '';
  const last = values.lastName ?? '';
  const fromFields = `${first} ${last}`.trim();
  return fromFields || person.name;
};

interface Props {
  people: SpendApprovalPerson[];
  values: PersonValues;
  onChange: (values: PersonValues) => void;
  /** Read-only: no pencil, values shown as facts. */
  locked?: boolean;
}

const SpendApprovalPeople: React.FC<Props> = ({
  people,
  values,
  onChange,
  locked,
}) => {
  const [editing, setEditing] = useState<number | null>(null);
  const editable = useMemo(
    () => people.map((p) => (p.fields?.length ?? 0) > 0),
    [people],
  );

  const setValue = (index: number, key: string, value: string) => {
    const next = values.map((v, i) =>
      i === index ? { ...v, [key]: value } : v,
    );
    onChange(next);
  };

  return (
    <ul className='spend-sheet-people'>
      {people.map((person, index) => {
        const personValues = values[index] ?? {};
        const fields = person.fields ?? [];
        const isEditing = editing === index && !locked;
        const nameFields = fields.filter(
          (f) => f.key === 'firstName' || f.key === 'lastName',
        );
        const otherFields = fields.filter(
          (f) => f.key !== 'firstName' && f.key !== 'lastName',
        );
        const missing = fields.filter(
          (f) => f.required && !(personValues[f.key] ?? '').trim(),
        );

        return (
          <li
            className={`spend-sheet-person${isEditing ? ' spend-sheet-person--editing' : ''}`}
            key={`${person.name}-${index}`}
          >
            <div className='spend-sheet-person-head'>
              <span className='spend-sheet-person-name'>
                {displayName(person, personValues)}
              </span>
              {editable[index] && !locked && (
                <button
                  type='button'
                  className='spend-sheet-person-edit'
                  aria-label={
                    isEditing
                      ? `Done editing ${displayName(person, personValues)}`
                      : `Edit ${displayName(person, personValues)}`
                  }
                  aria-pressed={isEditing}
                  onClick={() => setEditing(isEditing ? null : index)}
                >
                  {isEditing ? (
                    <CheckIcon size={14} />
                  ) : (
                    <PencilIcon size={14} />
                  )}
                </button>
              )}
            </div>

            {isEditing ? (
              <div className='spend-sheet-person-fields'>
                {[...nameFields, ...otherFields].map((field) => {
                  const id = `spend-person-${index}-${field.key}`;
                  const value = personValues[field.key] ?? '';
                  return (
                    <label
                      className='spend-sheet-person-field'
                      key={field.key}
                      htmlFor={id}
                    >
                      <span className='spend-sheet-person-field-label'>
                        {field.label}
                        {field.required && !value.trim() && (
                          <span className='spend-sheet-person-field-required'>
                            {' '}
                            · needed
                          </span>
                        )}
                      </span>
                      <input
                        id={id}
                        type={inputType(field.type)}
                        inputMode={field.type === 'phone' ? 'tel' : undefined}
                        autoComplete='off'
                        className='spend-sheet-person-input'
                        value={value}
                        placeholder={placeholder(field)}
                        onChange={(e) =>
                          setValue(index, field.key, e.target.value)
                        }
                      />
                      {field.source && (
                        <span className='spend-sheet-person-source'>
                          from {field.source}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            ) : fields.length > 0 ? (
              <dl className='spend-sheet-facts'>
                {otherFields.map((field) => {
                  const value = personValues[field.key] ?? '';
                  const unchanged = value === field.value;
                  const shown =
                    unchanged && field.display ? field.display : value;
                  return (
                    <div className='spend-sheet-fact' key={field.key}>
                      <dt>{field.label}</dt>
                      <dd
                        className={
                          shown ? undefined : 'spend-sheet-fact--missing'
                        }
                      >
                        {shown || 'needed'}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            ) : (
              person.facts &&
              person.facts.length > 0 && (
                <dl className='spend-sheet-facts'>
                  {person.facts.map((fact) => (
                    <div className='spend-sheet-fact' key={fact.label}>
                      <dt>{fact.label}</dt>
                      <dd>{fact.value}</dd>
                    </div>
                  ))}
                </dl>
              )
            )}

            {!isEditing && missing.length > 0 && (
              <span className='spend-sheet-person-source spend-sheet-person-source--warn'>
                Add {missing.map((f) => f.label.toLowerCase()).join(', ')}{' '}
                before approving
              </span>
            )}
            {!isEditing && person.source && (
              <span className='spend-sheet-person-source'>{person.source}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default SpendApprovalPeople;
